/**
 * 键盘配置加载器
 * 支持多系统、多布局、多语言的键盘配置加载和合并
 *
 * 新架构：
 * 1. 只维护 Windows 基础配置（keyboard.json, keyboard-eu.json）
 * 2. 语言差异文件应用在基础配置上
 * 3. Mac 配置通过系统转换器自动生成
 */

import winKeyboard from './keyboard.json'
import euKeyboard from './keyboard-eu.json'
import { applySystemTransform } from './systemTransformer.js'

import frenchDiff from './languages/french.json'
import portugueseDiff from './languages/portuguese.json'
import brazilDiff from './languages/brazil.json'
import spainDiff from './languages/spain.json'
/**
 * 语言配置映射
 * 只包含非默认语言的差异
 */
const languageDiffMap = {
  default: null,
  fr: frenchDiff,
  pt: portugueseDiff,
  br: brazilDiff,
  sp: spainDiff
}

/**
 * 合并键盘配置和语言差异
 * @param {Array} baseKeyboard - 基础键盘配置
 * @param {Array} languageDiff - 语言差异配置
 * @returns {Array} 合并后的键盘配置
 */
function mergeKeyboards(baseKeyboard, languageDiff) {
  if (!languageDiff || languageDiff.length === 0) {
    return baseKeyboard
  }

  // 第1步：合并已存在的键
  const mergedKeys = baseKeyboard.map(key => {
    const diff = languageDiff.find(d => d.code === key.code)
    if (diff) {
      // 合并差异中的所有属性，覆盖基础配置中的对应属性
      return { ...key, ...diff }
    }
    return key
  })

  // 第2步：添加新键（在语言差异中存在但在基础配置中不存在的键）
  const baseKeyCodes = new Set(baseKeyboard.map(k => k.code))
  const newKeys = languageDiff.filter(diff => !baseKeyCodes.has(diff.code))

  // 第3步：合并所有键并过滤隐藏的键
  return [...mergedKeys, ...newKeys].filter(key => !key.hidden)
}

/**
 * 根据系统、布局和语言加载键盘配置
 * @param {string} system - 系统类型: 'windows' | 'mac'
 * @param {string} layout - 布局类型: 'standard' | 'eu'
 * @param {string} language - 语言: 'default' | 'fr' | 'pt' | 'br' | 'sp'
 * @returns {Array} 完整的键盘配置数组
 */
export function loadKeyboard(system = 'windows', layout = 'standard', language = 'default') {
  // 验证参数
  if (!['windows', 'mac'].includes(system)) {
    console.warn(`Invalid system: ${system}, defaulting to 'windows'`)
    system = 'windows'
  }

  if (!['standard', 'eu'].includes(layout)) {
    console.warn(`Invalid layout: ${layout}, defaulting to 'standard'`)
    layout = 'standard'
  }

  if (!Object.keys(languageDiffMap).includes(language)) {
    console.warn(`Invalid language: ${language}, defaulting to 'default'`)
    language = 'default'
  }

  // 欧洲语言（fr, pt, sp）仅适用于 EU 布局
  // US 键盘（standard）选择这些语言时，强制使用默认布局
  const euOnlyLanguages = ['fr', 'pt', 'sp']
  if (layout === 'standard' && euOnlyLanguages.includes(language)) {
    console.log(`语言 ${language} 仅适用于 EU 键盘布局，US 键盘将使用默认布局`)
    language = 'default'
  }

  // 选择基础键盘配置（始终使用 Windows 配置作为基础）
  let baseKeyboard
  if (layout === 'eu') {
    baseKeyboard = euKeyboard
  } else {
    baseKeyboard = winKeyboard
  }

  // 获取语言差异
  const languageDiff = languageDiffMap[language]

  // 第1步：合并语言配置（如果有）
  const mergedKeyboard = mergeKeyboards(baseKeyboard, languageDiff)

  // 第2步：应用系统转换（Mac 转换）
  const finalKeyboard = applySystemTransform(mergedKeyboard, system)

  return finalKeyboard
}

/**
 * 获取所有支持的语言列表
 * @returns {Array} 语言列表
 */
export function getSupportedLanguages() {
  return Object.keys(languageDiffMap)
}

/**
 * 获取所有支持的系统列表
 * @returns {Array} 系统列表
 */
export function getSupportedSystems() {
  return ['windows', 'mac']
}

/**
 * 获取所有支持的布局列表
 * @returns {Array} 布局列表
 */
export function getSupportedLayouts() {
  return ['standard', 'eu']
}
